import {Link} from 'react-router';
import {twMerge} from 'tailwind-merge';

import MailIcon from '@/assets/icons/mail.svg?react';
import AppLogo from '@/shared/components/AppLogo';
import {BUMPY_GROUP, type LegalInfo} from '@/shared/consts/legal';
import {SUPPORT_EMAIL} from '@/shared/consts/socialLinks';
import {APP_ROUTES} from '@/utils_legacy/routes';

import {LOGIN_FOOTER_GROUP, LOGIN_FOOTER_ICONS} from './consts';

export type FooterProps = {
  hasShadow?: boolean;
  legal?: LegalInfo;
  footerLinks?: Array<{link: APP_ROUTES; title: string}>;
  contactLink?: APP_ROUTES;
};

const Footer = ({
  hasShadow = false,
  legal = BUMPY_GROUP,
  footerLinks = LOGIN_FOOTER_GROUP,
  contactLink = APP_ROUTES.ContactsGroup,
}: FooterProps) => {
  return (
    <footer>
      <section
        className={twMerge(
          'flex flex-col justify-between gap-10 bg-white px-14 py-14 shadow-md sm:flex-row sm:px-11 sm:py-28 xl:px-32',
          hasShadow && 'mt-10 shadow-[0_-2px_4px_rgba(0,0,0,0.1)]'
        )}
      >
        <div className="flex grow flex-col justify-between gap-10 sm:flex-row sm:flex-wrap">
          <div className="flex flex-col gap-6">
            <span className="font-semibold text-xl">Legal</span>
            <ul className="flex flex-col gap-4">
              {footerLinks.map((item) => (
                <li key={item.title}>
                  <a
                    href={item.link}
                    className="cursor-pointer font-normal text-black hover:text-black"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.title}
                  </a>
                </li>
              ))}
              {/*TODO uncomment when all will be ready*/}
              {/*<li>*/}
              {/*  <button*/}
              {/*    type="button"*/}
              {/*    className="cursor-pointer text-left font-normal text-black hover:text-black"*/}
              {/*    onClick={openCookiePreferences}*/}
              {/*  >*/}
              {/*    Cookie Preferences*/}
              {/*  </button>*/}
              {/*</li>*/}
            </ul>
          </div>
          <div className="flex flex-col gap-6 sm:justify-end">
            <span className="font-semibold text-xl">Company</span>
            <div className="flex flex-col gap-4">
              <a
                href={contactLink}
                className="cursor-pointer font-normal text-black hover:text-black"
                target="_blank"
                rel="noopener noreferrer"
              >
                Contacts
              </a>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex cursor-pointer items-center font-normal text-black hover:text-black"
              >
                <MailIcon className="mr-2 fill-dark-gray" />
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
        </div>
        <div className="flex grow justify-end">
          <div className="flex h-full w-full flex-col items-center justify-end gap-8 sm:w-fit sm:items-end">
            <Link to="/" className="sm:self-end">
              <AppLogo className="h-auto w-[120px] cursor-pointer text-primary sm:w-[120px]" />
            </Link>
            <div className="flex flex-col gap-7 sm:self-start">
              <span className="text-center font-semibold text-xl sm:w-full sm:text-start">
                Follow us
              </span>
              <ul className="flex gap-8">
                {LOGIN_FOOTER_ICONS.map((item) => (
                  <li key={item.link}>
                    <a
                      className="z-50 cursor-pointer"
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.link}
                    >
                      <item.icon />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      <section className="h-fit bg-light-primary p-8 py-7 text-center text-dark-gray text-xs sm:text-sm">
        <p>
          Copyright © {__BUILD_YEAR__} {legal?.company}, all rights reserved.
        </p>
        <p>{legal?.address}</p>
      </section>
    </footer>
  );
};

export default Footer;
